import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

function getTravelRequestStatusBadgeClass(status) {
  switch (status) {
    case "Approved":
      return "status-badge status-badge-approved";
    case "Under Review":
      return "status-badge status-badge-review";
    case "Rejected":
      return "status-badge status-badge-rejected";
    case "Cancelled":
      return "status-badge status-badge-cancelled";
    case "Draft":
      return "status-badge status-badge-draft";
    case "Pending":
    default:
      return "status-badge status-badge-pending";
  }
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleDateString();
}

const approvalActions = ["Pending", "Approved", "Rejected", "Cancelled"];

function TravelRequestDetailPage() {
  const history = useHistory();
  const { uuid } = useParams();
  const [request, setRequest] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    action: "Pending",
    comments: "",
    saving: false
  });
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPageData();
  }, [uuid]);

  const requester = useMemo(() => {
    return users.find((user) => String(user.id) === String(request?.user_id || ""));
  }, [request, users]);

  async function fetchPageData() {
    setLoading(true);
    setError("");

    try {
      const [requestResponse, usersResponse] = await Promise.all([
        api.get(`/travel-requests/${uuid}`),
        api.get("/users")
      ]);

      setRequest(requestResponse.data);
      setUsers(usersResponse.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load travel request details.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);

    try {
      await api.delete(`/travel-requests/${uuid}`);
      toast.success("Travel request deleted");
      history.push("/travel-requests");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete travel request.");
      setDeleting(false);
    }
  }

  function openApprovalModal() {
    setApprovalForm({
      action: "Pending",
      comments: "",
      saving: false
    });
    setShowApprovalModal(true);
  }

  function closeApprovalModal() {
    setShowApprovalModal(false);
    setApprovalForm({
      action: "Pending",
      comments: "",
      saving: false
    });
  }

  async function handleCreateApproval() {
    setApprovalForm((current) => ({
      ...current,
      saving: true
    }));

    try {
      await api.post(`/travel-requests/${uuid}/approvals`, {
        action: approvalForm.action,
        comments: approvalForm.comments.trim()
      });
      toast.success("Approval added");
      await fetchPageData();
      closeApprovalModal();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save approval.");
      setApprovalForm((current) => ({
        ...current,
        saving: false
      }));
    }
  }

  function renderDetailItem(label, value) {
    return {
      label,
      value: value ?? "-"
    };
  }

  function renderDetailRows(items) {
    const rows = [];

    for (let index = 0; index < items.length; index += 2) {
      const left = items[index];
      const right = items[index + 1];

      rows.push(
        <tr key={left.label}>
          <th>{left.label}</th>
          <td>{left.value}</td>
          {right ? (
            <>
              <th>{right.label}</th>
              <td>{right.value}</td>
            </>
          ) : (
            <>
              <th className="staff-task-detail-empty"></th>
              <td className="staff-task-detail-empty"></td>
            </>
          )}
        </tr>
      );
    }

    return rows;
  }

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: "Travel Requests", to: "/travel-requests" },
          { label: request?.destination || "Travel Request Details" }
        ]}
        title="Travel Request Details"
        subtitle="Review the request details, approvals, and planned travel information from one page."
        action={
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => history.push("/travel-requests")}>
              Back to travel requests
            </Button>
            <Button variant="primary" onClick={() => history.push(`/travel-requests/${uuid}/edit`)}>
              Edit request
            </Button>
            <Button variant="outline-danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting..." : "Delete request"}
            </Button>
          </div>
        }
      />

      {loading ? <LoadingState label="Loading travel request details..." /> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}

      {!loading && request ? (
        <div className="d-flex flex-column gap-4">
          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Core details</h5>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0 staff-task-detail-table">
                <tbody>
                  {renderDetailRows([
                    renderDetailItem(
                      "Requested By",
                      requester ? `${requester.firstname} ${requester.lastname}`.trim() || requester.username : request.user_id
                    ),
                    renderDetailItem("Status", <span className={getTravelRequestStatusBadgeClass(request.status)}>{request.status || "-"}</span>),
                    renderDetailItem("Destination", request.destination || "-"),
                    renderDetailItem("Travel Date", request.travel_from || "-"),
                    renderDetailItem("Return Date", request.travel_to || "-"),
                    renderDetailItem("Number of days", request.no_of_days ? String(request.no_of_days) : "-"),
                    renderDetailItem("Created", formatDate(request.created_at)),
                    renderDetailItem("Last updated", formatDate(request.last_updated))
                  ])}
                </tbody>
              </table>
            </div>
          </div>

          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Purpose</h5>
            <p className="mb-0 text-muted">{request.travel_purpose || "No travel purpose provided."}</p>
          </div>

          <div className="content-card activity-detail-section">
            <h5 className="mb-3">Expected output</h5>
            <p className="mb-0 text-muted">{request.expected_output || "No expected output provided."}</p>
          </div>

          <div className="content-card activity-detail-section">
            <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
              <h5 className="mb-0">Approvals</h5>
              <Button size="sm" onClick={openApprovalModal}>
                Add approval
              </Button>
            </div>
            {request.approvals?.length ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Approved By</th>
                      <th>Status</th>
                      <th>Comments</th>
                      <th>Approved On</th>
                    </tr>
                  </thead>
                  <tbody>
                    {request.approvals.map((approval) => {
                      const approver = users.find((user) => String(user.id) === String(approval.approver_id));

                      return (
                        <tr key={approval.uuid}>
                          <td>{approver ? `${approver.firstname} ${approver.lastname}`.trim() || approver.username : approval.approver_id}</td>
                          <td>{approval.action || "-"}</td>
                          <td>{approval.comments || "-"}</td>
                          <td>{approval.actioned_at ? formatDate(approval.actioned_at) : "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="mb-0 text-muted">No approvals have been added for this request yet.</p>
            )}
          </div>
        </div>
      ) : null}

      <Modal show={showApprovalModal} onHide={closeApprovalModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add approval</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-column gap-3">
            <div>
              <label className="form-label">Approval action</label>
              <Form.Select
                value={approvalForm.action}
                onChange={(event) =>
                  setApprovalForm((current) => ({
                    ...current,
                    action: event.target.value
                  }))
                }
              >
                {approvalActions.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div>
              <label className="form-label">Comments</label>
              <Form.Control
                as="textarea"
                rows={4}
                value={approvalForm.comments}
                onChange={(event) =>
                  setApprovalForm((current) => ({
                    ...current,
                    comments: event.target.value
                  }))
                }
                placeholder="Enter approval comments"
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={closeApprovalModal}>
            Cancel
          </Button>
          <Button onClick={handleCreateApproval} disabled={approvalForm.saving}>
            {approvalForm.saving ? "Saving..." : "Save approval"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default TravelRequestDetailPage;
